/**
 * Types pour le module Immobilisations (Fixed Assets)
 * Conforme au backend fixed-assets controller
 * 
 * Gestion du cycle de vie des immobilisations avec amortissement
 * automatique (linéaire + dégressif OHADA) — SYSCOHADA
 */

// ====== ENUMS ======

/** Méthode d'amortissement */
export type DepreciationMethod = 'linear' | 'degressive';

/** Statut d'une immobilisation */
export type FixedAssetStatus = 'active' | 'disposed' | 'fully_depreciated';

// ====== INTERFACES ======

/** Immobilisation complète (réponse API) */
export interface FixedAsset {
  id: string;
  name: string;
  reference?: string;
  description?: string;

  // Comptes associés
  assetAccountId: string;
  assetAccountCode?: string;
  depreciationAccountId: string;
  depreciationAccountCode?: string;
  depreciationExpenseAccountId: string;
  depreciationExpenseAccountCode?: string;

  // Données financières
  acquisitionCost: number;
  residualValue: number;
  depreciationMethod: DepreciationMethod;
  usefulLifeYears: number;
  acquisitionDate: string;

  // État
  status: FixedAssetStatus;
  cumulativeDepreciation: number;
  netBookValue: number;
  disposalDate?: string;

  // Contexte
  fiscalYearId: string;
  companyId: string;
  businessUnitId?: string;

  // Audit
  createdAt: string;
  updatedAt: string;
}

/** Payload de création d'une immobilisation */
export interface CreateFixedAssetPayload {
  name: string;
  reference?: string;
  description?: string;
  assetAccountId: string;
  assetAccountCode?: string;
  depreciationAccountId: string;
  depreciationAccountCode?: string;
  depreciationExpenseAccountId: string;
  depreciationExpenseAccountCode?: string;
  acquisitionCost: number;
  residualValue?: number;
  depreciationMethod: DepreciationMethod;
  usefulLifeYears: number;
  acquisitionDate: string;
  fiscalYearId: string;
  companyId: string;
  businessUnitId?: string;
}

/** Payload de mise à jour d'une immobilisation */
export interface UpdateFixedAssetPayload {
  name?: string;
  reference?: string;
  description?: string;
  assetAccountId?: string;
  assetAccountCode?: string;
  depreciationAccountId?: string;
  depreciationAccountCode?: string;
  depreciationExpenseAccountId?: string;
  depreciationExpenseAccountCode?: string;
  acquisitionCost?: number;
  residualValue?: number;
  depreciationMethod?: DepreciationMethod;
  usefulLifeYears?: number;
  acquisitionDate?: string;
  disposalDate?: string;
  businessUnitId?: string;
}

/** Payload pour la génération d'amortissements */
export interface GenerateDepreciationPayload {
  year: number;
  month: number;
  fiscalYearId: string;
}

/** Payload pour la cession d'une immobilisation (POST /fixed-assets/:id/dispose) */
export interface DisposeFixedAssetPayload {
  disposalDate: string;
  disposalProceeds: number;
  proceedsAccountId: string;          // Compte trésorerie qui reçoit l'encaissement
  vncAccountCode: string;             // Code SYSCOHADA classe 81 (VNC cessions)
  proceedsRevenueAccountCode: string; // Code SYSCOHADA classe 82 (produit cession)
  reference?: string;
  notes?: string;
}

/** Payload pour la mise au rebut d'une immobilisation (POST /fixed-assets/:id/scrap) */
export interface ScrapFixedAssetPayload {
  scrapDate: string;
  vncAccountCode: string;             // Code SYSCOHADA classe 81
  reason?: string;
}

/** Résultat d'une cession ou mise au rebut */
export interface FixedAssetLifecycleResult {
  asset: FixedAsset;
  transactionId: string;
  journalEntryId: string;
}

/** Type d'opération sur une immobilisation (historique) */
export type FixedAssetTransactionType =
  | 'acquisition'
  | 'depreciation'
  | 'disposal'
  | 'scrap'
  | 'revaluation';

/** Une opération chronologique sur une immobilisation */
export interface FixedAssetTransaction {
  id: string;
  assetId: string;
  type: FixedAssetTransactionType;
  date: string;
  amount: number;
  netBookValue?: number;
  gainLoss?: number;
  journalEntryId?: string;
  description?: string;
  companyId: string;
  fiscalYearId?: string;
  createdBy?: string;
  createdAt: string;
}

/** Résultat d'une génération d'amortissements */
export interface DepreciationJournalItem {
  assetId: string;
  assetName: string;
  journalId: string;
  monthlyAmount: number;
  method: DepreciationMethod;
}

export interface DepreciationResult {
  generatedCount: number;
  totalAmount: number;
  journals: DepreciationJournalItem[];
}

// ====== LABELS & HELPERS ======

export const DEPRECIATION_METHOD_LABELS: Record<DepreciationMethod, string> = {
  linear: 'Linéaire',
  degressive: 'Dégressif OHADA',
};

export const FIXED_ASSET_STATUS_LABELS: Record<FixedAssetStatus, string> = {
  active: 'Actif',
  disposed: 'Cédé',
  fully_depreciated: 'Totalement amorti',
};

export const FIXED_ASSET_STATUS_COLORS: Record<FixedAssetStatus, { bg: string; text: string }> = {
  active:             { bg: 'bg-green-100 dark:bg-green-900/30',  text: 'text-green-700 dark:text-green-400' },
  disposed:           { bg: 'bg-gray-100 dark:bg-gray-800',       text: 'text-gray-600 dark:text-gray-400' },
  fully_depreciated:  { bg: 'bg-blue-100 dark:bg-blue-900/30',    text: 'text-blue-700 dark:text-blue-400' },
};
